import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert } from 'antd'
import { TrendingUp, TrendingDown } from 'lucide-react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'
import { CustomReportInfoboxSection } from '../../../../../redux/context/customReports/typesSection'
import { KeyFigureVariable, ThresholdValueSetting } from '../../../../../redux/context/customReports/typesVariable'
import { DisplayScale } from '../../../../../shared/utils/numberScale'
import { VariableType } from '../../../../../components/Table/types'
import { formatVariableValue, ResolvedFormat } from '../grid/utils/valueFormatting'
import {
  periodGroupLabel as formatPeriodGroupLabel,
  periodLabel as formatPeriodLabel
} from '../../../../../redux/context/fiscalYears/utils'
import { parseDate } from '../../../../../config/dayjsConfig'
import { mapBalancesToInfobox, InfoboxDisplayData } from './infoboxDataMapper'
import { INFOBOX_IDS, SPARKLINE_PREFIX } from './infoboxConstants'
import { hexToRgb } from '../../../../../utils/color.utils'

import { contextCompanyCurrencySelector } from '../../../../../redux/context/company/selectors'

interface InfoboxCardProps {
  section: CustomReportInfoboxSection
  title?: string
  printMode?: boolean
}

function formatCompact(value: number | undefined, format: ResolvedFormat): string {
  if (value == null) return '—'
  return formatVariableValue(value, format.numberFormat, format.variableType, format.currency) || '—'
}

function formatSigned(value: number | undefined, format: ResolvedFormat): string {
  if (value == null) return ''
  const formatted = formatCompact(value, format)
  if (formatted === '—') return ''
  const sign = value > 0 ? '+' : ''
  return `${sign}${formatted}`
}

function resolveInfoboxDisplayFormat(variable?: KeyFigureVariable): {
  format: ResolvedFormat
  thresholdValueSetting: ThresholdValueSetting
} {
  return {
    format: {
      numberFormat: {
        decimals: variable?.style?.numberFormat?.decimals ?? 0,
        displayScale: DisplayScale.auto,
        // Pure user preference — formatVariableValue derives absolute/percentage/ratio
        // behavior from variableType itself, so no type logic needs mixing in here.
        showUnit: variable?.style?.numberFormat?.showUnit !== false
      },
      variableType: variable?.type
    },
    thresholdValueSetting: variable?.style?.thresholdValueSetting ?? ThresholdValueSetting.unset
  }
}

// --- Sub-components ---

const InfoboxBadge: React.FC<{
  growthPercent: number | undefined
  thresholdValueSetting: ThresholdValueSetting
}> = ({ growthPercent, thresholdValueSetting }) => {
  if (thresholdValueSetting === ThresholdValueSetting.unset || growthPercent === undefined) return null

  const isPositive = growthPercent >= 0
  const isGood = thresholdValueSetting === ThresholdValueSetting.biggerBetter ? isPositive : !isPositive

  const color = isGood ? '#52c41a' : '#f5222d'
  const bgColor = isGood ? '#f6ffed' : '#fff2f0'
  const Icon = isPositive ? TrendingUp : TrendingDown

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        color,
        backgroundColor: bgColor
      }}
    >
      <Icon size={12} />
      {formatSigned(growthPercent, { numberFormat: { decimals: 1 }, variableType: VariableType.percentage })}
    </span>
  )
}

const InfoboxValueBlock: React.FC<{
  label?: string
  data: {
    currentValue: number | undefined
    comparisonValue: number | undefined
    growthPercent: number | undefined
    diffAbsolute: number | undefined
  }
  format: ResolvedFormat
  comparisonLabel?: string
  thresholdValueSetting?: ThresholdValueSetting
}> = ({ label, data, format, comparisonLabel, thresholdValueSetting }) => {
  const growthColor = (() => {
    if (thresholdValueSetting == null || thresholdValueSetting === ThresholdValueSetting.unset) return '#595959'
    if (data.growthPercent == null) return '#595959'
    const isPositive = data.growthPercent >= 0
    const isGood = thresholdValueSetting === ThresholdValueSetting.biggerBetter ? isPositive : !isPositive
    return isGood ? '#52c41a' : '#f5222d'
  })()

  return (
    <div style={{ flex: 1 }}>
      {label && <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>{label}</div>}
      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{formatCompact(data.currentValue, format)}</div>
      {(data.growthPercent !== undefined || data.diffAbsolute !== undefined) && (
        <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
          {data.growthPercent !== undefined && (
            <span style={{ color: growthColor, fontWeight: 600 }}>
              {formatSigned(data.growthPercent, {
                numberFormat: { decimals: 1 },
                variableType: VariableType.percentage
              })}
            </span>
          )}
          {data.diffAbsolute !== undefined && (
            <span style={{ marginLeft: data.growthPercent !== undefined ? 6 : 0 }}>
              {formatSigned(data.diffAbsolute, format)}
            </span>
          )}
          {comparisonLabel && <span style={{ marginLeft: 6, color: '#8c8c8c' }}>{comparisonLabel}</span>}
        </div>
      )}
    </div>
  )
}

const hexToRgba = (hex: string, alpha: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return `rgba(0,0,0,${alpha})`
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`
}

const InfoboxSparkline: React.FC<{
  data: number[]
  labels?: string[]
  color?: string
  format: ResolvedFormat
  printMode?: boolean
}> = ({ data, labels = [], color = '#1890ff', format, printMode }) => {
  const { variableType, currency } = format
  const { decimals, showUnit } = format.numberFormat
  const containerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(50)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(([entry]) => {
      const h = Math.floor(entry.contentRect.height)
      if (h > 0) setHeight(h)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const options: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: 'area',
        height,
        margin: [2, 0, 2, 0],
        backgroundColor: 'transparent'
      },
      title: { text: undefined },
      xAxis: { visible: false, categories: labels },
      yAxis: { visible: false },
      legend: { enabled: false },
      tooltip: {
        enabled: true,
        outside: true,
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          // eslint-disable-next-line react/no-this-in-sfc
          const label = labels[this.point.index] ?? ''
          // eslint-disable-next-line react/no-this-in-sfc
          const value = formatVariableValue(this.y ?? undefined, { decimals, showUnit }, variableType, currency) || '—'
          return `<b>${label}</b><br/>${value}`
        },
        useHTML: true
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      plotOptions: {
        area: {
          marker: { enabled: false },
          lineWidth: 1.5,
          color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, hexToRgba(color, 0.2)],
              [1, hexToRgba(color, 0)]
            ]
          }
        }
      },
      series: [
        {
          type: 'area' as const,
          data: data.map((v, i) => {
            const point: Highcharts.PointOptionsObject = { y: v === 0 ? null : v }
            if (i === data.length - 1 && v !== 0) {
              point.marker = { enabled: true, radius: 3, symbol: 'circle' }
            }
            return point
          })
        }
      ]
    }),
    // format is destructured above into primitives — depend on those, not the object reference,
    // since the caller passes a new `format` literal every render.
    [data, labels, color, variableType, decimals, currency, showUnit, height]
  )

  const finalOptions = printMode
    ? Highcharts.merge(options, {
        exporting: { enabled: false },
        tooltip: { enabled: false },
        plotOptions: { series: { animation: false, enableMouseTracking: false } }
      })
    : options

  if (data.length === 0) return null

  return (
    <div ref={containerRef} style={{ height: '100%' }}>
      <HighchartsReact highcharts={Highcharts} options={finalOptions} />
    </div>
  )
}

const InfoboxEntityFooter: React.FC<{ section: CustomReportInfoboxSection }> = ({ section }) => {
  const names = useMemo(() => {
    const actual = section.categories?.find(c => c.id === INFOBOX_IDS.actual)
    const companies = actual?.children?.filter(c => c.type === 'company') ?? []

    // Single company with dimensions: show dimension names instead
    if (companies.length === 1) {
      const dimensions = companies[0].children?.filter(c => c.type === 'dimension') ?? []
      if (dimensions.length > 0) {
        return dimensions.map(d => d.dimension?.name ?? d.value).filter(Boolean) as string[]
      }
    }

    return companies.map(c => c.company?.name ?? c.value).filter(Boolean) as string[]
  }, [section.categories])

  if (names.length === 0) return null

  const maxShow = 2
  const shown = names.slice(0, maxShow)
  const remaining = names.length - maxShow

  return (
    <div
      style={{
        fontSize: 11,
        color: '#8c8c8c',
        marginTop: 8,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {shown.join(' + ')}
      {remaining > 0 && ` +${remaining}`}
    </div>
  )
}

function findPeriodGroup(cat: any): any {
  if (cat?.type === 'periodGroup') return cat
  for (const child of cat?.children ?? []) {
    const found = findPeriodGroup(child)
    if (found) return found
  }
  return undefined
}

function categoryLabel(cat: any): string {
  const pg = findPeriodGroup(cat)
  if (!pg) return ''
  const start = parseDate(pg.startDate)
  const end = parseDate(pg.endDate)
  if (start && end && start.month() === end.month() && start.year() === end.year()) {
    return formatPeriodLabel({ month: start.month() + 1, year: start.year() } as any)
  }
  return formatPeriodGroupLabel(pg)
}

// --- Main component ---

const InfoboxCard: React.FC<InfoboxCardProps> = ({ section, title, printMode }) => {
  const { t } = useTranslation()
  const currency = useSelector(contextCompanyCurrencySelector)
  const variable = section.variables?.[0] as KeyFigureVariable | undefined
  const data: InfoboxDisplayData = useMemo(() => mapBalancesToInfobox(variable?.balances), [variable?.balances])

  const { format: baseFormat, thresholdValueSetting } = resolveInfoboxDisplayFormat(variable)
  const format: ResolvedFormat = { ...baseFormat, currency }

  const comparisonCategory = section.categories?.find(c => c.id === INFOBOX_IDS.comparison)
  const comparisonLabel = useMemo(() => {
    if (!comparisonCategory) return undefined
    const pg = findPeriodGroup(comparisonCategory)
    if (!pg) return undefined

    if (pg.dataType === 'budget') return t('customReportPage:vsBudget')
    if (pg.dataType === 'forecast') return t('customReportPage:vsForecast')
    if (pg.offset === -12) return t('customReportPage:vsPrevYear')
    if (pg.offset === -1) return t('customReportPage:vsPrevMonth')
    return undefined
  }, [comparisonCategory, t])

  const actualCategory = section.categories?.find(c => c.id === INFOBOX_IDS.actual)
  const comboActualCategory = section.categories?.find(c => c.id === INFOBOX_IDS.comboActual)

  const mainPeriodLabel = useMemo(() => categoryLabel(actualCategory), [actualCategory])
  const comboPeriodLabel = useMemo(() => categoryLabel(comboActualCategory), [comboActualCategory])

  const sparklineLabels = useMemo(() => {
    const sparklineCats = (section.categories ?? [])
      .filter(c => c.id?.startsWith(SPARKLINE_PREFIX))
      .sort((a, b) => {
        const idxA = parseInt(a.id!.split('-').pop()!, 10)
        const idxB = parseInt(b.id!.split('-').pop()!, 10)
        return idxA - idxB
      })
    return sparklineCats.map(c => categoryLabel(c))
  }, [section.categories])

  return (
    <div
      data-cy={`infobox-card-${section.id}`}
      style={{ position: 'relative', padding: 8, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        {title && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: '#8c8c8c',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0
            }}
          >
            {title}
          </div>
        )}
        <InfoboxBadge growthPercent={data.growthPercent} thresholdValueSetting={thresholdValueSetting} />
      </div>

      {/* Errors */}
      {data.errors.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('customReportPage:dynamicSelectionError')}
          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1, margin: 0 }}
          banner
        />
      )}

      {/* Values */}
      <div>
        {data.combo ? (
          <div style={{ display: 'flex', gap: 16 }}>
            <InfoboxValueBlock
              label={mainPeriodLabel}
              data={data}
              format={format}
              comparisonLabel={comparisonLabel}
              thresholdValueSetting={thresholdValueSetting}
            />
            <div style={{ width: 1, backgroundColor: '#f0f0f0' }} />
            <InfoboxValueBlock
              label={comboPeriodLabel}
              data={data.combo}
              format={format}
              comparisonLabel={comparisonLabel}
              thresholdValueSetting={thresholdValueSetting}
            />
          </div>
        ) : (
          <InfoboxValueBlock
            label={mainPeriodLabel}
            data={data}
            format={format}
            comparisonLabel={comparisonLabel}
            thresholdValueSetting={thresholdValueSetting}
          />
        )}
      </div>

      {/* Sparkline */}
      <div style={{ flex: 1, minHeight: 50, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <InfoboxSparkline
            data={data.sparklineData}
            labels={sparklineLabels}
            color={variable?.style?.color}
            format={format}
            printMode={printMode}
          />
        </div>
      </div>

      {/* Footer */}
      <InfoboxEntityFooter section={section} />
    </div>
  )
}

export default InfoboxCard
